import {
  CanvasTexture,
  CylinderGeometry,
  Group,
  type Material,
  Mesh,
  MeshStandardMaterial,
  type Object3D,
  type Scene,
  SphereGeometry,
  Sprite,
  SpriteMaterial,
  type Vector3,
} from 'three';
import type { LevelCollider } from '../collision/LevelCollider';
import { type BotRoute, createRouteRunner, type RouteRunner } from '../game/bot';
import { RunTimer } from '../game/RunTimer';
import { PlayerController } from '../movement/PlayerController';
import { TUNING_DEFAULTS } from '../movement/tuning';
import type { CombatWorld } from './CombatWorld';

/** A target dummy a map declares (spec 0026). */
export interface DummySpec {
  id: string;
  /** Feet position (static dummies stand here; patrol dummies spawn here). */
  position: Vector3;
  maxHealth: number;
  respawnSec: number;
  /**
   * Optional deterministic movement: the dummy drives a real PlayerController
   * through this route; the runner is recreated when the route completes, so
   * the same input sequence loops forever.
   */
  patrol?: { route: BotRoute; startYaw?: number };
}

const DUMMY_RADIUS = 20;
const DUMMY_HEIGHT = 72;
const BAR_WIDTH = 110;
const BAR_HEIGHT = 14;

/** A world-space health bar: a canvas-textured sprite (shared with DuelistChampion). */
export interface HealthBarSprite {
  sprite: Sprite;
  canvas: HTMLCanvasElement;
  texture: CanvasTexture;
}

export function buildHealthBar(offsetY: number): HealthBarSprite {
  const canvas = document.createElement('canvas');
  canvas.width = 128;
  canvas.height = 20;
  drawHealthBar(canvas, 1);
  const texture = new CanvasTexture(canvas);
  const sprite = new Sprite(new SpriteMaterial({ map: texture, depthTest: false }));
  sprite.scale.set(BAR_WIDTH, BAR_HEIGHT, 1);
  sprite.position.y = offsetY;
  sprite.renderOrder = 3;
  return { sprite, canvas, texture };
}

/**
 * Frees the geometry and materials under a combatant's group. Each actor builds
 * its own body material and sprite material, so a map switch that only disposed
 * geometry left those (and their textures) resident on the GPU.
 */
export function disposeGroup(root: Object3D): void {
  root.traverse((obj) => {
    if (obj instanceof Mesh || obj instanceof Sprite) {
      if (obj instanceof Mesh) {
        obj.geometry.dispose();
      }
      const material = obj.material as Material | Material[];
      if (Array.isArray(material)) {
        for (const m of material) m.dispose();
      } else {
        material.dispose();
      }
    }
  });
}

export function drawHealthBar(canvas: HTMLCanvasElement, fraction: number): void {
  const ctx = canvas.getContext('2d');
  if (!ctx) return;
  ctx.clearRect(0, 0, canvas.width, canvas.height);
  ctx.fillStyle = 'rgba(8,11,15,0.85)';
  ctx.fillRect(0, 0, canvas.width, canvas.height);
  ctx.fillStyle = fraction > 0.35 ? '#e04747' : '#ff8a3d';
  ctx.fillRect(2, 2, Math.max(0, (canvas.width - 4) * fraction), canvas.height - 4);
}

interface DummyActor {
  spec: DummySpec;
  position: Vector3; // live feet (patrol dummies update it)
  group: Group;
  bar: Sprite;
  barCanvas: HTMLCanvasElement;
  barTexture: CanvasTexture;
  lastBarHealth: number;
  controller?: PlayerController;
  runner?: RouteRunner;
}

/**
 * Presenter + driver for target dummies (spec 0026): builds the meshes and
 * world-space health bars, registers each dummy into the CombatWorld, and
 * steps patrol dummies one fixed tick at a time. Death hides the dummy; the
 * CombatWorld's respawn timer brings it back at full health.
 */
export class TargetDummies {
  private readonly actors: DummyActor[] = [];

  constructor(
    private readonly scene: Scene,
    private readonly combat: CombatWorld,
  ) {}

  load(specs: DummySpec[], collider: LevelCollider, killY: number): void {
    this.clear();
    for (const spec of specs) {
      const actor = this.buildActor(spec, collider, killY);
      this.actors.push(actor);
      this.scene.add(actor.group);
      this.combat.register({
        id: spec.id,
        team: 'red',
        maxHealth: spec.maxHealth,
        radius: DUMMY_RADIUS,
        height: DUMMY_HEIGHT,
        getPosition: () => actor.position,
        respawnSec: spec.respawnSec,
      });
    }
  }

  /** Fixed-tick step: drive patrols and mirror alive/dead visibility. */
  tick(_dt: number): void {
    for (const actor of this.actors) {
      const state = this.combat.state(actor.spec.id);
      const alive = state?.alive ?? true;
      actor.group.visible = alive;
      if (!alive) continue;
      if (actor.controller && actor.runner && actor.spec.patrol) {
        if (actor.runner.stepMany(1)) {
          // Route finished — restart the same input sequence (deterministic loop).
          actor.runner = createRouteRunner(
            actor.controller,
            new RunTimer([]),
            actor.spec.patrol.route,
            actor.runner.currentYaw(),
          );
        }
        actor.position.copy(actor.controller.position);
        actor.group.position.copy(actor.position);
      }
    }
  }

  /** Render-side: redraw health bars that changed (cheap canvas fill). */
  updateBars(): void {
    for (const actor of this.actors) {
      const state = this.combat.state(actor.spec.id);
      if (!state || state.health === actor.lastBarHealth) continue;
      actor.lastBarHealth = state.health;
      drawHealthBar(actor.barCanvas, state.health / actor.spec.maxHealth);
      actor.barTexture.needsUpdate = true;
    }
  }

  clear(): void {
    for (const actor of this.actors) {
      this.scene.remove(actor.group);
      disposeGroup(actor.group);
      actor.barTexture.dispose();
    }
    this.actors.length = 0;
  }

  private buildActor(spec: DummySpec, collider: LevelCollider, killY: number): DummyActor {
    const group = new Group();
    const body = new MeshStandardMaterial({
      color: 0xb03a3a,
      emissive: 0x8a1f1f,
      emissiveIntensity: 0.45,
      roughness: 0.6,
    });
    const torso = new Mesh(
      new CylinderGeometry(DUMMY_RADIUS, DUMMY_RADIUS, DUMMY_HEIGHT - DUMMY_RADIUS, 12),
      body,
    );
    torso.position.y = (DUMMY_HEIGHT - DUMMY_RADIUS) / 2;
    const head = new Mesh(new SphereGeometry(DUMMY_RADIUS * 0.8, 12, 10), body);
    head.position.y = DUMMY_HEIGHT;
    group.add(torso, head);

    const {
      sprite: bar,
      canvas: barCanvas,
      texture: barTexture,
    } = buildHealthBar(DUMMY_HEIGHT + 40);
    group.add(bar);

    const position = spec.position.clone();
    group.position.copy(position);

    const actor: DummyActor = {
      spec,
      position,
      group,
      bar,
      barCanvas,
      barTexture,
      lastBarHealth: spec.maxHealth,
    };
    if (spec.patrol) {
      // Frozen default feel (spec 0027): a dummy must never inherit the
      // player's hero delta or active ultimate buffs from the live singleton.
      actor.controller = new PlayerController(collider, spec.position.clone(), killY, {
        ...TUNING_DEFAULTS,
      });
      actor.runner = createRouteRunner(
        actor.controller,
        new RunTimer([]),
        spec.patrol.route,
        spec.patrol.startYaw ?? 0,
      );
    }
    return actor;
  }
}
